/**
 * CourseSearchSelect
 *
 * A searchable course combobox built on Radix Popover so it works correctly
 * inside Radix Dialog without Radix's DismissableLayer blocking clicks.
 *
 * Handles both field-naming conventions:
 *   • /courses API          → { name, code }
 *   • /student-dashboard    → { courseName, courseCode }
 *
 * Props:
 *   courses      – array of course objects
 *   value        – currently selected course _id (string)
 *   onChange     – (courseId: string) => void
 *   placeholder  – string (default "Select course")
 *   disabled     – boolean
 *   showFees     – boolean — when true, shows onlineFee / offlineFee in the list
 *   disabledIds  – string[] — course IDs that should appear greyed-out / unselectable
 *   disabledLabel – string appended after disabled items (e.g. "— Already Enrolled")
 *   loading      – boolean
 */

import * as Popover from '@radix-ui/react-popover';
import { ChevronDown, Search, X } from 'lucide-react';
import React, { useRef, useState } from 'react';

const CourseSearchSelect = ({
  courses = [],
  value,
  onChange,
  placeholder = 'Select course',
  disabled = false,
  showFees = false,
  disabledIds = [],
  disabledLabel = '',
  loading = false,
}) => {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const triggerRef          = useRef(null);

  // Normalise a course object so the rest of the component works uniformly
  const norm = (c) => ({
    id:         String(c._id || c.id || ''),
    name:       c.name || c.courseName || '',
    code:       c.code || c.courseCode || '',
    onlineFee:  c.onlineFee  ?? c.fees?.online?.totalAmount  ?? null,
    offlineFee: c.offlineFee ?? c.fees?.offline?.totalAmount ?? null,
    weekendFee: c.weekendFee ?? c.fees?.weekend?.totalAmount ?? null,
  });

  const normalized     = courses.map(norm);
  const selectedCourse = normalized.find(c => c.id === value);

  const filtered = normalized.filter(c => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q);
  });

  const handleSelect = (id) => {
    if (disabledIds.includes(id)) return;
    onChange(id);
    setOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearch('');
  };

  const handleOpenChange = (next) => {
    if (disabled) return;
    setOpen(next);
    if (!next) setSearch('');
  };

  const CourseButton = ({ c }) => {
    const isSelected = c.id === value;
    const isDisabled = disabledIds.includes(c.id);
    return (
      <li key={c.id}>
        <button
          type="button"
          onClick={() => handleSelect(c.id)}
          disabled={isDisabled}
          className={[
            'w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-3',
            'transition-colors',
            isSelected  ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50',
            isDisabled  ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
          ].join(' ')}
        >
          <div className="min-w-0">
            <div className="font-medium truncate">
              {c.name}
              {isDisabled && disabledLabel && (
                <span className="ml-1 text-xs text-gray-400 font-normal">{disabledLabel}</span>
              )}
            </div>
            {c.code && (
              <div className="text-xs text-gray-400 mt-0.5">{c.code}</div>
            )}
          </div>
          {showFees && (c.onlineFee != null || c.offlineFee != null || c.weekendFee != null) && (
            <div className="text-xs text-gray-500 text-right flex-shrink-0">
              {c.onlineFee  != null && <div>Online: ₹{Number(c.onlineFee).toLocaleString()}</div>}
              {c.offlineFee != null && <div>Offline: ₹{Number(c.offlineFee).toLocaleString()}</div>}
              {c.weekendFee != null && <div>Weekend: ₹{Number(c.weekendFee).toLocaleString()}</div>}
            </div>
          )}
        </button>
      </li>
    );
  };

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          className="w-full h-9 px-3 text-sm flex items-center justify-between rounded-md border border-input bg-background hover:bg-accent/50 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className={`truncate ${selectedCourse ? 'text-foreground' : 'text-muted-foreground'}`}>
            {selectedCourse ? selectedCourse.name : placeholder}
          </span>
          <div className="flex items-center gap-1 ml-2 flex-shrink-0">
            {value && !disabled && (
              <span
                role="button"
                tabIndex={-1}
                onClick={handleClear}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </span>
            )}
            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
          </div>
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className="z-[9999] bg-white border border-gray-200 rounded-md shadow-lg outline-none overflow-hidden"
          style={{
            width: 'var(--radix-popover-trigger-width)',
            minWidth: 'var(--radix-popover-trigger-width)',
            maxWidth: 'min(var(--radix-popover-trigger-width), 90vw)',
          }}
          sideOffset={4}
          align="start"
          avoidCollisions
          collisionPadding={8}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {/* Search input */}
          <div className="p-2 border-b border-gray-100">
            <div className="flex items-center gap-1.5 border border-gray-200 rounded-md px-2 py-1 bg-gray-50 focus-within:border-blue-400 focus-within:bg-white transition-colors">
              <Search className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name or code…"
                className="flex-1 text-xs bg-transparent outline-none text-gray-700 placeholder-gray-400"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>

          {/* Options list */}
          <ul className="max-h-56 overflow-y-auto py-1">
            {loading ? (
              <li className="py-6 text-center text-xs text-gray-400">Loading courses…</li>
            ) : normalized.length === 0 ? (
              <li className="py-6 text-center text-xs text-gray-400">No courses available</li>
            ) : filtered.length === 0 ? (
              <li className="py-6 text-center text-xs text-gray-500">No courses match "{search}"</li>
            ) : (
              <>
                {!search && (
                  <li className="px-3 py-1 text-[10px] text-gray-400 font-medium uppercase tracking-wide border-b border-gray-100 bg-gray-50/60">
                    All courses ({normalized.length})
                  </li>
                )}
                {filtered.map(c => <CourseButton key={c.id} c={c} />)}
              </>
            )}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default CourseSearchSelect;
